import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, Eye, EyeOff } from "lucide-react";

export default function AdminBlogList() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/blog");
      setPosts(data || []);
    } catch (e) {
      toast.error("Failed to load posts.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const togglePublish = async (p) => {
    try {
      await api.put(`/admin/blog/${p.id}`, { published: !p.published });
      toast.success(p.published ? "Unpublished." : "Published.");
      load();
    } catch (e) {
      toast.error("Update failed.");
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete "${p.title}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/blog/${p.id}`);
      toast.success("Deleted.");
      load();
    } catch (e) {
      toast.error("Delete failed.");
    }
  };

  return (
    <div data-testid="admin-blog-list">
      <div className="flex items-end justify-between mb-8">
        <div>
          <div className="font-mono-accent text-[10px] uppercase text-[#4A5550]">◆ Blog Manager</div>
          <h1 className="font-display font-black uppercase tracking-tighter text-3xl md:text-5xl text-[#0A0E0C] leading-none mt-1">
            Posts
          </h1>
        </div>
        <Link
          to="/admin/blog/new"
          className="inline-flex items-center gap-2 bg-[#FF5A00] hover:bg-[#E04F00] text-white font-display font-bold uppercase text-sm tracking-wider px-5 py-3 border-2 border-[#122A1E] hard-shadow-sm hover-lift"
          data-testid="admin-blog-new"
        >
          <Plus size={16} /> New Post
        </Link>
      </div>

      {loading ? (
        <div className="font-mono-accent text-sm text-[#4A5550]">Loading…</div>
      ) : posts.length === 0 ? (
        <div className="border-2 border-[#122A1E] bg-[#EAEAE5] p-10 text-center">
          <div className="font-display font-black uppercase">No posts yet</div>
          <p className="text-[#4A5550] mt-2">Create your first article.</p>
        </div>
      ) : (
        <div className="border-2 border-[#122A1E] bg-[#F4F4F1] overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-[#122A1E] text-[#F4F4F1] font-mono-accent text-[10px] uppercase">
              <tr>
                <th className="text-left px-4 py-3">Title</th>
                <th className="text-left px-4 py-3 hidden md:table-cell">Slug</th>
                <th className="text-left px-4 py-3 hidden md:table-cell">Tag</th>
                <th className="text-left px-4 py-3">Status</th>
                <th className="text-right px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {posts.map((p, i) => (
                <tr
                  key={p.id}
                  className={i % 2 ? "bg-[#EAEAE5]" : ""}
                  data-testid={`admin-post-row-${p.slug}`}
                >
                  <td className="px-4 py-3 font-display font-bold text-[#0A0E0C]">
                    {p.title}
                  </td>
                  <td className="px-4 py-3 font-mono-accent text-[10px] hidden md:table-cell text-[#4A5550]">
                    /{p.slug}
                  </td>
                  <td className="px-4 py-3 font-mono-accent text-[10px] hidden md:table-cell text-[#FF5A00] uppercase">
                    {p.tag || "—"}
                  </td>
                  <td className="px-4 py-3">
                    {p.published ? (
                      <span className="font-mono-accent text-[10px] uppercase bg-[#FF5A00] text-white px-2 py-1">
                        Published
                      </span>
                    ) : (
                      <span className="font-mono-accent text-[10px] uppercase bg-[#D2CEBD] text-[#4A5550] px-2 py-1">
                        Draft
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="inline-flex gap-2">
                      <button
                        onClick={() => togglePublish(p)}
                        className="border-2 border-[#122A1E] px-2 py-1 hover:bg-[#122A1E] hover:text-white"
                        title={p.published ? "Unpublish" : "Publish"}
                        data-testid={`admin-toggle-${p.slug}`}
                      >
                        {p.published ? <EyeOff size={14} /> : <Eye size={14} />}
                      </button>
                      <Link
                        to={`/admin/blog/${p.id}`}
                        className="border-2 border-[#122A1E] px-2 py-1 hover:bg-[#122A1E] hover:text-white"
                        data-testid={`admin-edit-${p.slug}`}
                      >
                        <Pencil size={14} />
                      </Link>
                      <button
                        onClick={() => remove(p)}
                        className="border-2 border-[#122A1E] px-2 py-1 hover:bg-red-600 hover:text-white hover:border-red-600"
                        data-testid={`admin-delete-${p.slug}`}
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
